/** Constantes partagées par l'ensemble des composants du portail. */
/** Taille maximale du code d'artifact accepté par le formulaire (§3.2 : 1 Mo). */
export const MAX_ARTIFACT_CODE_BYTES = 1_048_576;
/** Extensions de fichier acceptées au dépôt (drag & drop, §3.2). */
export const ACCEPTED_ARTIFACT_EXTENSIONS = ['.jsx', '.tsx', '.html', '.js', '.ts'];
/**
 * Dépendances autorisées dans les applications générées (§5.1).
 * Sert à la fois de consigne dans le prompt de transformation, de liste
 * `external` pour la validation esbuild et de garde-fou au scaffolding.
 *
 * Cette liste doit rester **strictement alignée** sur les dépendances déclarées
 * dans `packages/app-template/template/package.json` : une dépendance autorisée
 * ici mais absente du gabarit passerait la validation puis ferait échouer le
 * build Docker. Un test du gabarit vérifie cet alignement.
 */
export const ALLOWED_APP_DEPENDENCIES = [
    'react',
    'react-dom',
    'react-dom/client',
    'clsx',
    'd3',
    'date-fns',
    'framer-motion',
    'lodash',
    'lucide-react',
    'mathjs',
    'papaparse',
    'recharts',
    'tailwind-merge',
    'zustand',
];
/**
 * Origines externes que peuvent charger les applications déployées, réparties
 * selon la directive de la politique de sécurité qui les admet.
 *
 * Source **unique** de trois endroits qui doivent dire la même chose : la
 * politique de sécurité injectée dans les pages (`app-template`), la validation
 * qui refuse une page non conforme (`builder`), et le prompt qui l'annonce au
 * modèle. Leur divergence coûte cher — le prompt a présenté `fonts.gstatic.com`
 * comme autorisé alors que la politique le bloquait : le modèle produisait du
 * Google Fonts, la validation le refusait, et une réécriture entière repartait
 * pour rien.
 *
 * Le rôle est porté ici parce qu'une origine ne sert pas à tout : Google
 * n'héberge des feuilles de style que sur `fonts.googleapis.com` et des
 * fichiers de police que sur `fonts.gstatic.com`. Les confondre dans une liste
 * plate autoriserait un `<script>` depuis un service de polices.
 *
 * Google Fonts y figure sur décision d'exploitation : les artifacts en font un
 * usage constant, et une police de substitution défigure la page — ce que le
 * §5.1 interdit puisque la transformation doit préserver le rendu. La
 * contrepartie est connue et assumée : l'adresse IP des visiteurs est transmise
 * à Google. La lever supposerait de rapatrier les fichiers de police à la
 * construction et de les servir depuis l'application elle-même.
 */
export const ALLOWED_EXTERNAL_SOURCES = {
    script: ['https://cdnjs.cloudflare.com'],
    style: ['https://cdnjs.cloudflare.com', 'https://fonts.googleapis.com'],
    font: ['https://cdnjs.cloudflare.com', 'https://fonts.gstatic.com'],
};
/**
 * Union des origines autorisées, tous rôles confondus.
 *
 * C'est ce dont a besoin la validation du HTML : elle relève les attributs
 * `src` et `href` sans savoir ce que chaque URL désigne. Le cloisonnement par
 * directive, lui, est appliqué par la politique de sécurité du navigateur.
 */
export const ALLOWED_EXTERNAL_ORIGINS = [
    ...new Set([
        ...ALLOWED_EXTERNAL_SOURCES.script,
        ...ALLOWED_EXTERNAL_SOURCES.style,
        ...ALLOWED_EXTERNAL_SOURCES.font,
    ]),
];
/**
 * Préfixe de l'API de données des applications déployées (§11).
 *
 * Il apparaît en **deux** endroits qui doivent dire la même chose : le mini-serveur
 * du gabarit, qui y monte ses routes, et le prompt, qui l'annonce au modèle. Les
 * faire diverger produirait un code applicatif qui appelle une route inexistante,
 * et un 404 pour seule explication — un test vérifie donc leur accord.
 *
 * Ce chemin n'apparaît **pas** dans le manifeste Cloudron : la protection porte sur
 * l'application entière, et non sur ce seul préfixe (voir `app-template/manifest`).
 */
export const DATA_API_PREFIX = '/api/data';
/** Nombre maximal de fichiers produits par la transformation. */
export const MAX_TRANSFORMED_FILES = 40;
/**
 * Nombre maximal de changements fonctionnels retenus dans la réponse du modèle.
 *
 * Au-delà, la liste cesse d'être lisible : une personne à qui l'on présente
 * trente points à valider ne les lit pas, elle clique. Le surplus est écarté et
 * l'écart signalé plutôt que masqué.
 */
export const MAX_FUNCTIONAL_CHANGES = 12;
/** Longueur maximale d'un libellé de changement fonctionnel. */
export const MAX_FUNCTIONAL_CHANGE_LENGTH = 400;
/** Taille cumulée maximale des fichiers produits par la transformation (2 Mo). */
export const MAX_TRANSFORMED_TOTAL_BYTES = 2 * 1_048_576;
/** Point d'entrée exigé pour une application React. */
export const REACT_ENTRYPOINTS = ['src/App.jsx', 'src/App.tsx'];
/** Point d'entrée exigé pour un site statique. */
export const STATIC_ENTRYPOINT = 'index.html';
/** Longueur maximale du commentaire libre associé à une application (§3.2). */
export const MAX_COMMENT_LENGTH = 2000;
/** Longueur maximale du commentaire de version, obligatoire à chaque mise à jour (§3.5). */
export const MAX_VERSION_COMMENT_LENGTH = 500;
/**
 * Longueur maximale d'une demande de modification en langage naturel.
 *
 * Assez pour décrire précisément plusieurs changements, trop peu pour y coller un
 * fichier — ce qui serait le détourner de son objet : coller du code se fait dans
 * le champ prévu, où l'on voit ce qu'on envoie.
 */
export const MAX_MODIFICATION_INSTRUCTION_LENGTH = 2000;
/**
 * Longueur maximale de la description d'une application à créer.
 *
 * Plus généreuse que celle d'une modification, et pour une raison : décrire une
 * application entière — ce qu'elle affiche, pour qui, avec quelles règles de
 * calcul — demande davantage de place que demander un changement sur une
 * application qui existe déjà et que le modèle a sous les yeux.
 */
export const MAX_APP_DESCRIPTION_LENGTH = 6000;
/**
 * Longueur minimale d'une description exploitable.
 *
 * « un tableau de bord » ne décrit pas une application : le modèle en inventerait
 * la totalité, et l'auteur recevrait quelque chose qu'il n'a pas demandé. Le seuil
 * ne garantit pas une bonne description, il écarte celles qui n'en sont pas.
 */
export const MIN_APP_DESCRIPTION_LENGTH = 30;
/**
 * Nombre de versions par application dont l'image est conservée dans le
 * registre (§5.5). Au-delà, les plus anciennes sont supprimées du registre et
 * les versions correspondantes marquées « archivées » : leur code reste
 * consultable et comparable, mais leur restauration directe n'est plus possible.
 */
export const DEFAULT_MAX_VERSIONS_PER_APP = 20;
/** Clés de quotas administrables (§6.5). */
export const QUOTA_KEYS = [
    'MAX_APPS_PER_USER',
    'MAX_MEMORY_MB_PER_APP',
    'DEPLOYS_PER_HOUR',
    'CLAUDE_PROXY_DAILY_LIMIT',
];
/**
 * Mémoire minimale acceptée par Cloudron pour une application.
 *
 * En deçà, l'installation est refusée avec « memoryLimit too small » — au moment
 * du déploiement, donc après la transformation, la construction et la
 * publication. Le quota est borné par cette valeur avant d'écrire le manifeste,
 * pour que le refus ne survienne pas si tard.
 */
export const CLOUDRON_MIN_MEMORY_MB = 512;
/** Valeurs par défaut des quotas, surchargeables globalement puis par utilisateur. */
export const DEFAULT_QUOTAS = {
    MAX_APPS_PER_USER: 5,
    /**
     * 512 Mo : c'est le plancher de Cloudron, pas une estimation de besoin. Un
     * site statique servi par le mini-serveur du gabarit en consomme une fraction.
     */
    MAX_MEMORY_MB_PER_APP: CLOUDRON_MIN_MEMORY_MB,
    DEPLOYS_PER_HOUR: 5,
    CLAUDE_PROXY_DAILY_LIMIT: 500,
};
/** Niveaux de log persistés pour les lignes rattachées à un job. */
export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'];
/** Nom de la file BullMQ partagée entre `portal-api` (producteur) et `builder` (consommateur). */
export const DEPLOYMENT_QUEUE_NAME = 'deployments';
/** Clé Redis mémorisant le volume de logs du dernier build réussi d'une application. */
export function buildReferenceKey(slug) {
    return `build:lines:${slug}`;
}
/** Canal Redis de diffusion des événements d'un déploiement (§D4 du plan). */
export function deploymentChannel(deploymentId) {
    return `deploy:events:${deploymentId}`;
}
/** Clé Redis du compteur de séquence des événements d'un déploiement. */
export function deploymentSeqKey(deploymentId) {
    return `deploy:seq:${deploymentId}`;
}
//# sourceMappingURL=constants.js.map